// stafflist/dialogs/staffformdialog.js
import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Avatar,
  Badge,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
  CloudUploadOutlined,
  InfoOutlined,
  CheckCircle,
  Add,
} from "@mui/icons-material";
import { StaffService } from "../../../../services/staffandplant/staffService";
import {countryCodeOptions}  from "../../../../utils/content";

const StaffFormDialog = ({
  open,
  onClose,
  title,
  formData,
  onFieldChange,
  onDocumentsChange,
  onSubmit,
  loading,
  isValid,
  isEditMode,
}) => {
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const validateField = (field, value) => {
    if (
      ["firstname", "email", "profile_data.mobile_number"].includes(field) &&
      !value
    ) {
      return "This field is required";
    }
    if (field === "email" && value && !/\S+@\S+\.\S+/.test(value)) {
      return "Email is invalid";
    }
    if (
      field === "profile_data.mobile_number" &&
      value &&
      !/^\d+$/.test(value)
    ) {
      return "Mobile number should contain only digits";
    }
    return "";
  };

  const handleFieldChange = (field, value) => {
    onFieldChange(field, value);
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setSnackbar({
        open: true,
        message: "Invalid file type. Please upload an image.",
        severity: "error",
      });
      return;
    }

    setUploadingProfilePic(true);

    try {
      const uploadResult = await StaffService.uploadSingleFile(file, true);

      if (uploadResult?.id) {
        onFieldChange("profile_data.profile_url", uploadResult.id);

        // Update other_documents to include profile picture
        const currentDocs = formData.user.profile_data.other_documents || [];
        const docsWithoutProfilePic = currentDocs.filter(
          (doc) => doc.type !== 3
        );
        const profilePicDoc = {
          upload_id: uploadResult.id,
          type: 3,
          name: file.name || "Profile Picture",
        };
        docsWithoutProfilePic.push(profilePicDoc);
        onDocumentsChange(docsWithoutProfilePic);

        setSnackbar({
          open: true,
          message: "Profile image uploaded successfully.",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error uploading profile image:", error);
      setSnackbar({
        open: true,
        message: `Failed to upload profile image: ${error.message}`,
        severity: "error",
      });
    } finally {
      setUploadingProfilePic(false);
    }
  };

  const handleRemoveProfileImage = () => {
    if (profileImagePreview && profileImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(profileImagePreview);
    }
    setProfileImagePreview(null);
    setProfileImageFile(null);
    onFieldChange("profile_data.profile_url", null);

    // Remove from other_documents
    const currentDocs = formData.user.profile_data.other_documents || [];
    const docsWithoutProfilePic = currentDocs.filter((doc) => doc.type !== 3);
    onDocumentsChange(docsWithoutProfilePic);

    const fileInput = document.getElementById("profile-image-upload");
    if (fileInput) fileInput.value = "";
  };

  const handleFileUpload = async (type) => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";

    if (type === "otherDocs") {
      fileInput.multiple = true;
    }

    fileInput.accept =
      type === "photoId" || type === "profilePic"
        ? "image/*,.pdf"
        : "image/*,.pdf,.doc,.docx,.xls,.xlsx";

    fileInput.onchange = async (event) => {
      const files = Array.from(event.target.files);
      if (!files.length) return;

      try {
        if (type === "photoId") setUploadingPhoto(true);
        else setUploadingDocs(true);

        for (const file of files) {
          const uploadResult = await StaffService.uploadSingleFile(file, true);

          if (!uploadResult?.id) {
            console.error("Upload response missing upload_id:", uploadResult);
            setSnackbar({
              open: true,
              message: "Upload failed - missing upload ID",
              severity: "error",
            });
            continue;
          }

          if (type === "photoId") {
            onFieldChange("profile_data.photo_id", uploadResult.id);
            setSnackbar({
              open: true,
              message: "Photo ID uploaded successfully.",
              severity: "success",
            });
            break;
          } else {
            const newDocument = {
              upload_id: uploadResult.id,
              type: 1,
              name: file.name,
            };

            const currentDocs = [
              ...(formData.user.profile_data.other_documents || []),
            ];
            currentDocs.push(newDocument);
            onDocumentsChange(currentDocs);
          }
        }

        if (type === "otherDocs" && files.length > 0) {
          setSnackbar({
            open: true,
            message: `${files.length} document${
              files.length > 1 ? "s" : ""
            } uploaded successfully`,
            severity: "success",
          });
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        setSnackbar({
          open: true,
          message: `Error uploading file: ${error.message || "Unknown error"}`,
          severity: "error",
        });
      } finally {
        if (type === "photoId") setUploadingPhoto(false);
        else setUploadingDocs(false);
      }
    };

    fileInput.click();
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return "#E57373";
    const firstChar = name.charAt(0).toLowerCase();
    if (firstChar >= "a" && firstChar <= "m") {
      return "#E57373";
    } else if (firstChar >= "n" && firstChar <= "z") {
      return "#FFB74D";
    } else {
      return "#E57373";
    }
  };

  useEffect(() => {
    if (open && formData.user.profile_data.profile_url) {
      // Load existing profile image
      setProfileImagePreview(formData.user.profile_data.profile_url);
    } else if (!open) {
      // Cleanup on close
      if (profileImagePreview && profileImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(profileImagePreview);
      }
      setProfileImagePreview(null);
      setProfileImageFile(null);
    }

    return () => {
      if (profileImagePreview && profileImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(profileImagePreview);
      }
    };
    // eslint-disable-next-line
  }, [open, formData.user.profile_data.profile_url]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", sm: "500px", md: "600px" },
          border: "none",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              {title}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Stack spacing={3}>
            {/* Profile Image Section */}
            <Box display="flex" justifyContent="center">
              <Box sx={{ position: "relative" }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    <Box>
                      <input
                        id="profile-image-upload"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleProfileImageUpload}
                        disabled={uploadingProfilePic}
                      />
                      <IconButton
                        component="label"
                        htmlFor="profile-image-upload"
                        size="small"
                        sx={{
                          bgcolor: "#666",
                          color: "white",
                          "&:hover": { bgcolor: "#333" },
                          width: 36,
                          height: 36,
                          border: "2px solid white",
                        }}
                        disabled={uploadingProfilePic}
                      >
                        {uploadingProfilePic ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <CameraIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  }
                >
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      bgcolor: getAvatarColor(
                        formData.user.firstname + " " + formData.user.lastname
                      ),
                      border: "2px solid #e0e0e0",
                      fontSize: "2rem",
                      fontWeight: "bold",
                    }}
                    src={
                      profileImagePreview ||
                      formData.user.profile_data.profile_url
                    }
                  >
                    {!profileImagePreview &&
                      !formData.user.profile_data.profile_url &&
                      getInitials(
                        formData.user.firstname + " " + formData.user.lastname
                      )}
                  </Avatar>
                </Badge>
                {(formData.user.profile_data.profile_url ||
                  profileImageFile) && (
                  <IconButton
                    size="small"
                    onClick={handleRemoveProfileImage}
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      bgcolor: "#dc3545",
                      color: "white",
                      "&:hover": { bgcolor: "#c82333" },
                      width: 28,
                      height: 28,
                      border: "2px solid white",
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Form Fields */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstname"
                  value={formData.user.firstname || ""}
                  onChange={(e) =>
                    handleFieldChange("firstname", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  required
                  error={!!errors.firstname}
                  helperText={errors.firstname}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      "&:hover fieldset": { borderColor: "#1976D2" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastname"
                  value={formData.user.lastname || ""}
                  onChange={(e) =>
                    handleFieldChange("lastname", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      "&:hover fieldset": { borderColor: "#1976D2" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Job Title"
                  name="job_title"
                  value={formData.user.job_title}
                  onChange={(e) =>
                    handleFieldChange("job_title", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      "&:hover fieldset": { borderColor: "#1976D2" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.user.email}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  variant="outlined"
                  size="small"
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      "&:hover fieldset": { borderColor: "#1976D2" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="country-code-label">Country Code</InputLabel>
                  <Select
                    labelId="country-code-label"
                    value={formData.user.profile_data.country_code || "+91"}
                    label="Country Code"
                    onChange={(e) =>
                      handleFieldChange(
                        "profile_data.country_code",
                        e.target.value
                      )
                    }
                    error={!!errors["profile_data.country_code"]}
                    sx={{
                      borderRadius: 1,
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976D2",
                      },
                    }}
                  >
                    {countryCodeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="profile_data.mobile_number"
                  value={formData.user.profile_data.mobile_number || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "profile_data.mobile_number",
                      e.target.value
                    )
                  }
                  variant="outlined"
                  size="small"
                  required
                  error={!!errors["profile_data.mobile_number"]}
                  helperText={errors["profile_data.mobile_number"]}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      "&:hover fieldset": { borderColor: "#1976D2" },
                    },
                  }}
                />
              </Grid>

              {/* Documents Section */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  gutterBottom
                >
                  Documents
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Photo ID Upload Section */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Photo ID
                      </Typography>
                      <Tooltip title="Upload a photo ID document like passport, driver's license, etc.">
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        border: "1px dashed #ccc",
                        borderRadius: 2,
                        p: 3,
                        backgroundColor: "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#f0f7ff",
                          borderColor: "#1976D2",
                        },
                      }}
                      onClick={() =>
                        !uploadingPhoto && handleFileUpload("photoId")
                      }
                    >
                      {formData.user.profile_data.photo_id ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexGrow: 1,
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label="Photo ID Uploaded"
                              size="small"
                              color="success"
                              icon={<CheckCircle fontSize="small" />}
                              sx={{ mr: 2 }}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onFieldChange("profile_data.photo_id", null);
                            }}
                            sx={{ color: "#F44336" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            sx={{
                              backgroundColor: "#f0f7ff",
                              mb: 2,
                              width: 60,
                              height: 60,
                              pointerEvents: "none",
                            }}
                            disabled={uploadingPhoto}
                          >
                            {uploadingPhoto ? (
                              <CircularProgress size={24} />
                            ) : (
                              <CloudUploadOutlined
                                fontSize="large"
                                sx={{ color: "#1976D2" }}
                              />
                            )}
                          </IconButton>
                          <Typography
                            variant="body2"
                            align="center"
                            sx={{ mb: 1 }}
                          >
                            Click or Drop Files here
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            align="center"
                          >
                            Supported files: JPG, PNG, PDF
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Other Documents Upload Section */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Other Documents
                      </Typography>
                      <Tooltip title="Upload any additional documents like certificates, references, etc.">
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {formData.user.profile_data.other_documents?.length > 0 ? (
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          {formData.user.profile_data.other_documents
                            .filter((doc) => doc.type !== 3) // Exclude profile pictures
                            .map((doc, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  bgcolor: "#E3F2FD",
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                }}
                              >
                                <TextField
                                  value={doc.name || `Document ${index + 1}`}
                                  size="small"
                                  variant="outlined"
                                  onChange={(e) => {
                                    const newName = e.target.value;
                                    const newDocs = [
                                      ...formData.user.profile_data
                                        .other_documents,
                                    ];
                                    newDocs[index] = {
                                      ...newDocs[index],
                                      name: newName,
                                    };
                                    onDocumentsChange(newDocs);
                                  }}
                                  sx={{ flexGrow: 1 }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newDocs = [
                                      ...formData.user.profile_data
                                        .other_documents,
                                    ];
                                    newDocs.splice(index, 1);
                                    onDocumentsChange(newDocs);
                                  }}
                                  sx={{ color: "#F44336" }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ))}
                        </Box>

                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={
                            uploadingDocs ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Add />
                            )
                          }
                          onClick={() => handleFileUpload("otherDocs")}
                          disabled={uploadingDocs}
                          sx={{
                            textTransform: "none",
                            color: "#1976D2",
                            borderColor: "#1976D2",
                            "&:hover": {
                              borderColor: "#1976D2",
                              backgroundColor: "#f0f7ff",
                            },
                          }}
                        >
                          {uploadingDocs
                            ? "Uploading..."
                            : "Add More Documents"}
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          border: "1px dashed #ccc",
                          borderRadius: 2,
                          p: 3,
                          backgroundColor: "#fafafa",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            backgroundColor: "#f0f7ff",
                            borderColor: "#1976D2",
                          },
                        }}
                        onClick={() =>
                          !uploadingDocs && handleFileUpload("otherDocs")
                        }
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            sx={{
                              backgroundColor: "#f0f7ff",
                              mb: 2,
                              width: 60,
                              height: 60,
                              pointerEvents: "none",
                            }}
                            disabled={uploadingDocs}
                          >
                            {uploadingDocs ? (
                              <CircularProgress size={24} />
                            ) : (
                              <CloudUploadOutlined
                                fontSize="large"
                                sx={{ color: "#1976D2" }}
                              />
                            )}
                          </IconButton>
                          <Typography
                            variant="body2"
                            align="center"
                            sx={{ mb: 1 }}
                          >
                            Click or Drop Files here
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            align="center"
                          >
                            Supported files: JPG, PNG, PDF, DOC, DOCX, XLS, XLSX
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Box>

        {/* Footer Actions */}
        <Box sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                borderColor: "#666",
                color: "#666",
                "&:hover": { borderColor: "#333", color: "#333" },
                minWidth: 100,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              variant="contained"
              disabled={
                loading ||
                !isValid ||
                uploadingPhoto ||
                uploadingDocs ||
                uploadingProfilePic
              }
              sx={{
                bgcolor: "#E57373",
                "&:hover": { bgcolor: "#D32F2F" },
                "&:disabled": { bgcolor: "#999", color: "white", opacity: 0.7 },
                minWidth: 100,
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isEditMode ? (
                "Save Changes"
              ) : (
                "Add Staff"
              )}
            </Button>
          </Stack>
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Drawer>
  );
};

export default StaffFormDialog;
